// ── 배경 전환 상태 (module-level: 경쟁 조건 방지) ───────────────────────────
let _bgGen = 0;
let _bgInFlight: HTMLImageElement | null = null;
let _bgTimeout: ReturnType<typeof setTimeout> | null = null;
let _bgFadePending = false;
let _bgColorPending: string | null = null;
// 현재 전환 중인 "다음 값"을 기억 — 전환 취소 시 즉시 커밋하기 위해 사용
let _bgNextValue: string | null = null;
let _bgNextColorValue: string | null = null;
const _styleCache = new Map<string, string>();

const normalizeStyleValue = (setValue: string | number | undefined | null) =>
  setValue == null ? '' : String(setValue);

const getCachedOrInlineValue = (
  root: HTMLElement,
  variableName: string,
): string => {
  const cached = _styleCache.get(variableName);
  if (cached !== undefined) return cached;
  return root.style.getPropertyValue(`--${variableName}`);
};

export const getCachedGlobalStyleValue = (variableName: string) =>
  _styleCache.get(variableName) ?? '';

export const setGlobalStyleInstant = (
  variableName: string,
  setValue: string | number | undefined | null,
) => {
  const root = document.documentElement;
  const value = normalizeStyleValue(setValue);
  const currentValue = getCachedOrInlineValue(root, variableName);
  if (currentValue === value) return;
  root.style.setProperty(`--${variableName}`, value);
  _styleCache.set(variableName, value);
};

const resetPendingBackgroundTransition = () => {
  if (_bgInFlight) {
    _bgInFlight.onload = null;
    _bgInFlight.onerror = null;
    _bgInFlight = null;
  }
  if (_bgTimeout !== null) {
    clearTimeout(_bgTimeout);
    _bgTimeout = null;
  }
  _bgFadePending = false;
  _bgColorPending = null;
  _bgNextValue = null;
  _bgNextColorValue = null;
  _bgGen += 1;
};

export const applyGlobalBackgroundInstant = ({
  backgroundImgURL,
  backgroundColor = '',
}: {
  backgroundImgURL: string;
  backgroundColor?: string;
}) => {
  resetPendingBackgroundTransition();
  setGlobalStyleInstant('backgroundImgURL', backgroundImgURL);
  setGlobalStyleInstant('backgroundImgURLNext', 'none');
  setGlobalStyleInstant('bgFadeActive', '0');
  setGlobalStyleInstant('backgroundColor', backgroundColor);
  setGlobalStyleInstant('backgroundColorNext', '');
  setGlobalStyleInstant('bgHasInit', '1');
};

/**
 * CSS 변수를 전역으로 설정합니다.
 *
 * - backgroundImgURL : 이미지 프리로드 후 fade 전환 (body::before/::after 2-레이어 방식)
 * - backgroundColor  : backgroundImgURL 전환과 동기화. 전환이 없으면 즉시 적용
 * - 그 외 변수       : 값이 다를 때만 즉시 적용
 */
export function setGlobalStyle(
  variableName: string,
  setValue: string | number | undefined | null,
) {
  const root = document.documentElement;
  const value = normalizeStyleValue(setValue);

  // ─── backgroundImgURL ────────────────────────────────────────────────────
  if (variableName === 'backgroundImgURL') {
    if (getCachedOrInlineValue(root, 'backgroundImgURL') === value) return;
    // 이미 동일한 목표 배경으로 전환 중이면 취소/재시작하지 않는다.
    // /world 이탈 시 cleanup과 다음 페이지가 같은 home 배경을 연속 요청하면서
    // 첫 전환을 cancel→restart해 깜빡임을 유발하므로, 동일 target은 noop 처리.
    if ((_bgInFlight || _bgTimeout !== null || _bgFadePending) && _bgNextValue === value) {
      return;
    }

    // ── 진행 중인 전환이 있으면 "즉시 커밋" 후 새 전환 시작 ──
    // 오버레이를 단순히 꺼버리면 이전 이미지→흰 배경이 순간 노출되므로,
    // 대신 이전 전환의 목표값을 body::before에 즉시 적용하고 오버레이를 끈다.
    if (_bgInFlight || _bgTimeout !== null || _bgFadePending) {
      if (_bgInFlight) {
        _bgInFlight.onload = null;
        _bgInFlight.onerror = null;
        _bgInFlight = null;
      }
      if (_bgTimeout !== null) {
        clearTimeout(_bgTimeout);
        _bgTimeout = null;
      }
      _bgFadePending = false;
      // 이전 전환의 목표값을 body::before에 즉시 반영
      if (_bgNextValue !== null) {
        setGlobalStyleInstant('backgroundImgURL', _bgNextValue);
      }
      if (_bgNextColorValue !== null) {
        setGlobalStyleInstant('backgroundColor', _bgNextColorValue);
        setGlobalStyleInstant('backgroundColorNext', '');
      } else if (_bgColorPending !== null) {
        setGlobalStyleInstant('backgroundColor', _bgColorPending);
        setGlobalStyleInstant('backgroundColorNext', '');
      }
      _bgColorPending = null;
      _bgNextValue = null;
      _bgNextColorValue = null;
      setGlobalStyleInstant('bgFadeActive', '0');
      setGlobalStyleInstant('backgroundImgURLNext', 'none');
    }

    const gen = ++_bgGen;

    // 최초 1회: 애니메이션 없이 즉시 적용
    if (!getCachedOrInlineValue(root, 'bgHasInit')) {
      setGlobalStyleInstant('backgroundImgURL', value);
      if (_bgColorPending !== null) {
        setGlobalStyleInstant('backgroundColor', _bgColorPending);
        _bgColorPending = null;
      }
      setGlobalStyleInstant('bgHasInit', '1');
      return;
    }

    // 전환 대상 기록
    _bgNextValue = value;
    _bgNextColorValue = null;

    const commitFade = () => {
      setGlobalStyleInstant('backgroundImgURLNext', value);
      if (_bgColorPending !== null) {
        setGlobalStyleInstant('backgroundColorNext', _bgColorPending);
        _bgNextColorValue = _bgColorPending;
      }
      _bgFadePending = true;
      requestAnimationFrame(() => {
        _bgFadePending = false;
        if (gen !== _bgGen) return;
        setGlobalStyleInstant('bgFadeActive', '1');
        _bgTimeout = setTimeout(() => {
          _bgTimeout = null;
          if (gen !== _bgGen) return;
          setGlobalStyleInstant('backgroundImgURL', value);
          setGlobalStyleInstant('bgFadeActive', '0');
          setGlobalStyleInstant('backgroundImgURLNext', 'none');
          if (_bgColorPending !== null) {
            setGlobalStyleInstant('backgroundColor', _bgColorPending);
            setGlobalStyleInstant('backgroundColorNext', '');
            _bgColorPending = null;
          }
          _bgNextValue = null;
          _bgNextColorValue = null;
        }, 410);
      });
    };

    const match = /url\((?:'|")?(.*?)(?:'|")?\)/.exec(value);
    const url = match ? match[1] : '';

    if (!url) {
      commitFade();
      return;
    }

    const img = new Image();
    _bgInFlight = img;
    img.onload = () => {
      if (gen !== _bgGen) return;
      _bgInFlight = null;
      commitFade();
    };
    img.onerror = () => {
      if (gen !== _bgGen) return;
      _bgInFlight = null;
      setGlobalStyleInstant('backgroundImgURL', value);
      if (_bgColorPending !== null) {
        setGlobalStyleInstant('backgroundColor', _bgColorPending);
        _bgColorPending = null;
      }
      _bgNextValue = null;
      _bgNextColorValue = null;
    };
    img.src = url;
    return;
  }

  // ─── backgroundColor ─────────────────────────────────────────────────────
  if (variableName === 'backgroundColor') {
    if (getCachedOrInlineValue(root, 'backgroundColor') === value) return;
    if (_bgInFlight !== null || _bgTimeout !== null || _bgFadePending) {
      _bgColorPending = value;
      return;
    }
    setGlobalStyleInstant('backgroundColor', value);
    return;
  }

  // ─── 기타 CSS 변수 ───────────────────────────────────────────────────────
  if (getCachedOrInlineValue(root, variableName) !== value) {
    setGlobalStyleInstant(variableName, value);
  }
}
